import api from './authenticatedApiClient';

export const getPersonById = async (id) => {
    const response = await api.get(`/person/${id}`);
    return response.data;
};

export const getPublicPrerenderPerson = async (id) => {
    const response = await api.get(`/prerender/person/${id}`);
    return response.data;
};

export const getPersonDefaultPhoto = async (personID) => {
    const response = await api.get(`/person-default-photo/${personID}`);
    return response.data;
};

export const getPersonDnaEstimate = async (personID) => {
    const response = await api.get(`/person/${personID}/dna-estimate`);
    return response.data?.estimate || null;
};

export const getTotalPeople = async () => {
    const response = await api.get(`/total-people`);
    return response.data.total;
};

export const getTotalDocuments = async () => {
    const response = await api.get(`/total-documents`);
    return response.data.totalDocuments;
};

export const getTotalPhotos = async () => {
    const response = await api.get(`/total-photos`);
    return response.data.totalPhotos;
};

export const getUniqueLastnames = async () => {
    const response = await api.get(`/unique-lastnames`);
    return response.data.uniqueLastnames;
};

export const getParents = async (personID) => {
    const response = await api.get(`/parents/${personID}`);
    return response.data;
};

export const getAncestors = async (personID, generations = 5) => {
    const response = await api.get(`/ancestors/${personID}?generations=${generations}`);
    return response.data;
};

export const getRelationship = async (personID1, personID2, depth = 15) => {
    const response = await api.get(`/relationship/${personID1}/${personID2}`, {
        params: { depth },
    });
    return response.data;
};

export const getPatrilinealAncestors = async (personID) => {
    const response = await api.get(`/patrilineal/${personID}`);
    return response.data;
};

export const getMatrilinealAncestors = async (personID) => {
    const response = await api.get(`/matrilineal/${personID}`);
    return response.data;
};

export const getDescendants = async (personID, generations = 5, requestConfig = {}) => {
    // requestConfig allows callers to pass cancellation signals or custom headers
    // without duplicating endpoint-specific query logic.
    const response = await api.get(`/descendants/${personID}`, {
        params: { generations },
        ...requestConfig,
    });
    return response.data;
};

export const getPersonMedia = async (personID) => {
    const response = await api.get(`/media/${personID}`);
    return response.data;
};

export const getPersonPhotos = async (personID) => {
    const response = await api.get(`/photos/${personID}`);
    return response.data;
};

export const getPersonEvents = async (personID) => {
    const response = await api.get(`/events/${personID}`);
    return response.data;
};

export const getEventMedia = async (personID) => {
    const response = await api.get(`/event-media/${personID}`);
    return response.data;
};

export const getSiblings = async (personID) => {
    const response = await api.get(`/siblings/${personID}`);
    return response.data;
};

// export const getFamilies = async (personID) => {
//     const response = await api.get(`/families/${personID}`);
//     return response.data;
// };

export const getFamilyDetails = async (personID, requestConfig = {}) => {
    // Family details can be expensive; optional request config helps integrate with
    // pagination/abort logic in consumers that fetch many people quickly.
    const response = await api.get(`/families/${personID}/details`, requestConfig);
    return response.data;
};

// export const getChildren = async (personID) => {
//     const response = await api.get(`/children/${personID}`);
//     return response.data;
// };

// export const getFamilyInfo = async (personID) => {
//     const response = await api.get(`/family-info/${personID}`);
//     return response.data;
// };

// export const getFamilyDocuments = async (personID) => {
//     const response = await api.get(`/family-documents/${personID}`);
//     return response.data;
// };

export const isDirectAncestor = async (personID) => {
    const response = await api.get(`/is-direct-ancestor/${personID}`);
    return response.data;
};

export const getPersonHaplogroup = async (personID) => {
    const response = await api.get(`/person/${personID}/haplogroup`);
    return response.data;
};

export const getAutosomalDNA = async (personID) => {
    const response = await api.get(`/person/${personID}/atdna`);
    return response.data;
};

export const getPersonBranches = async (personID) => {
    const response = await api.get(`/branches/${personID}`);
    return response.data;    // an array of { branch, description }
};

export const getCitations = async (personID) => {
    const response = await api.get(`/citations/${personID}`);
    return response.data;
};

export const getNotes = async (personID) => {
    const response = await api.get(`/notes/${personID}`);
    return response.data;
};

export const getEventLocations = async (personID) => {
    try {
        // Each location source can fail independently, so we keep partial results by
        // soft-catching individual requests instead of failing the whole response.
        const personEventLocationsResponse = await api.get(`/person-event-locations/${personID}`).catch(() => ({ data: [] }));
        const eventLocationsResponse = await api.get(`/event-locations/${personID}`).catch(() => ({ data: [] }));
        const childrenBirthLocationsResponse = await api.get(`/children-birth-locations/${personID}`).catch(() => ({ data: [] }));
        const childrenBaptismLocationsResponse = await api.get(`/children-baptism-locations/${personID}`).catch(() => ({ data: [] }));

        return {
            personEventLocations: personEventLocationsResponse.data,
            eventLocations: eventLocationsResponse.data,
            childrenBirthLocations: childrenBirthLocationsResponse.data,
            childrenBaptismLocations: childrenBaptismLocationsResponse.data,
        };
    } catch (error) {
        console.error("Error fetching event locations:", error);
        return {
            personEventLocations: [],
            eventLocations: [],
            childrenBirthLocations: [],
            childrenBaptismLocations: [],
        };
    }
};

export const searchPeopleSuggestions = async (query) => {
    try {
        // Mirror backend minimum search length to avoid unnecessary roundtrips.
        if (!query || query.length < 2) {
            return { suggestions: [] };
        }
        const response = await api.get('/search/suggestions', { params: { q: query } });
        return response.data;
    } catch (error) {
        console.error("Error fetching person suggestions:", error);
        return { suggestions: [] };
    }
};

export const searchPeople = async (query, page = 1, limit = 10) => {
    try {
        // Return stable empty payload shape when query is too short so UI components
        // can render consistently without additional guards.
        if (!query || query.length < 2) {
            return { total: 0, results: [], page, limit };
        }
        const response = await api.get('/search', {
            params: { q: query, page, limit }
        });
        return response.data;
    } catch (error) {
        console.error("Error searching for people:", error);
        return { total: 0, results: [], page, limit };
    }
};
